import { Paper } from '@mui/material'
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { ServiceItem } from '../../hooks/useTransformServiceItems/useTransformServiceItems'
import { DELIMITER } from '../../models/Const'
import {
  useUpdateLaborOverrideMutation,
  useUpdatePartOverrideMutation
} from '../../redux/api'
import { ServiceInfoStatusEnum, VehicleEntry } from '../../sdk'
import {
  estimateServiceCardStyle,
  repairOrderServiceCardStyle
} from '../../styles/serviceCard'
import { ServiceCard } from './ServiceCard'
import { OrderCartDialog } from './useOrderCart/OrderCartDialog'
import { useOrderCart } from './useOrderCart/useOrderCart'

export const ServiceCardsOfRepairOrder = ({
  serviceItems,
  allServiceItems,
  isEstimate,
  readOnly,
  vehicle,
  createAuthorizationCallback
}: {
  serviceItems: ServiceItem[]
  allServiceItems: ServiceItem[]
  isEstimate: boolean
  readOnly: boolean
  vehicle: VehicleEntry | undefined
  createAuthorizationCallback?: () => void
}) => {
  const { accessToken } = useAccessTokenAndShopId()
  const [updateLaborOverride] = useUpdateLaborOverrideMutation()
  const [updatePartOverride] = useUpdatePartOverrideMutation()
  // TODO: this hook is supposed to order parts for all services, but it's broken
  const {
    handleOrderCart,
    orderPartAction,
    setOrderPartAction,
    isSubmitting,
    setIsSubmitting
  } = useOrderCart(
    allServiceItems.filter((s) => s.status !== ServiceInfoStatusEnum.Deferred)
  )
  const onDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result
    // dropped outside the list
    if (!destination) {
      return
    }
    const oldServiceId = source.droppableId
    const newServiceId = destination.droppableId
    if (oldServiceId === newServiceId) {
      return
    }
    const oldService = serviceItems.find((s) => s.id === oldServiceId)!
    const [itemType, itemId] = draggableId.split(DELIMITER)

    if (itemType === 'labor') {
      const oldLabor = oldService.laborOverrides!.find((l) => l.id === itemId)!
      await updateLaborOverride({
        authorization: accessToken,
        laborOverride: {
          ...oldLabor,
          service: newServiceId
        }
      })
    } else if (itemType === 'parts') {
      const oldPart = [
        ...oldService.partsClaimed.map((p) => p.overrideEntry),
        ...oldService.partsInCart.map((p) => p.overrideEntry),
        ...oldService.partsStandAlone.map((p) => p.overrideEntry),
        ...oldService.repairLinkPartsInCart.map((p) => p.overrideEntry)
      ].find((l) => l.uuid === itemId)!
      await updatePartOverride({
        authorization: accessToken,
        partOverride: {
          ...oldPart,
          service: newServiceId
        }
      })
    }
  }
  return (
    <>
      {orderPartAction && (
        <OrderCartDialog
          {...{
            orderPartAction,
            setOrderPartAction,
            isSubmitting,
            setIsSubmitting,
            vehicle
          }}
          services={allServiceItems}
        />
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        {serviceItems.map((serviceItem) =>
          serviceItem.status === ServiceInfoStatusEnum.Authorized ? (
            <Paper
              key={serviceItem.id}
              elevation={0}
              sx={
                isEstimate
                  ? estimateServiceCardStyle
                  : repairOrderServiceCardStyle[serviceItem.status as string]
              }
              data-testid="ServiceCard">
              <ServiceCard
                serviceItem={serviceItem}
                handleOrderCart={handleOrderCart}
                readOnly={readOnly}
                hasDroppableContext={false}
                showPrice={true}
                createAuthorizationCallback={createAuthorizationCallback}
              />
            </Paper>
          ) : (
            <Droppable droppableId={serviceItem.id} key={serviceItem.id}>
              {(provided, snapshot) => (
                <Paper
                  elevation={0}
                  sx={{
                    ...(isEstimate
                      ? estimateServiceCardStyle
                      : repairOrderServiceCardStyle[
                          serviceItem.status as string
                        ]),
                    boxShadow: snapshot.isDraggingOver
                      ? '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)'
                      : 'none'
                  }}
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  data-testid="ServiceCard">
                  <ServiceCard
                    serviceItem={serviceItem}
                    handleOrderCart={handleOrderCart}
                    readOnly={readOnly}
                    droppableProvided={provided}
                    hasDroppableContext={true}
                    showPrice={true}
                    createAuthorizationCallback={createAuthorizationCallback}
                  />
                </Paper>
              )}
            </Droppable>
          )
        )}
      </DragDropContext>
    </>
  )
}
